(() => {
  if (customElements.get('lv-project-cart')) return;
  const root = () => window.Shopify?.routes?.root || '/';
  const carts = () => [...document.querySelectorAll('lv-project-cart')];
  let queue = Promise.resolve(), busy = false, noteDraft, savedNote, noteTimer;
  const enqueue = task => { const result = queue.then(task); queue = result.catch(() => {}); return result; };
  const message = text => carts().forEach(cart => { cart.querySelector('[data-cart-message]').textContent = text; });
  const setBusy = value => {
    busy = value;
    carts().forEach(cart => {
      cart.setAttribute('aria-busy', String(value));
      cart.querySelectorAll('[data-quantity-step], [data-cart-quantity], [data-cart-checkout]').forEach(el => { el.disabled = value; });
      cart.querySelectorAll('[data-cart-remove]').forEach(el => el.setAttribute('aria-disabled', String(value)));
    });
  };
  async function post(path, body) {
    const response = await fetch(root() + path, {method:'POST',headers:{'Content-Type':'application/json','Accept':'application/json'},body:JSON.stringify(body)});
    const data = await response.json();
    if (!response.ok) throw new Error(typeof data.description === 'string' ? data.description : typeof data.message === 'string' ? data.message : '');
    return data;
  }
  function syncNote() {
    carts().forEach(cart => {
      const field = cart.querySelector('[name="note"]');
      if (!field) return;
      if (noteDraft !== undefined && field.value !== noteDraft) field.value = noteDraft;
      cart.querySelector('[data-note-count]').textContent = `${field.value.length}/500`;
    });
  }
  async function saveNote() {
    clearTimeout(noteTimer);
    const value = noteDraft;
    if (value === undefined || value === savedNote) return;
    carts().forEach(cart => { const status = cart.querySelector('[data-note-status]'); if (status) status.textContent = cart.dataset.saving; });
    await post('cart/update.js', {note:value});
    savedNote = value;
    carts().forEach(cart => { const status = cart.querySelector('[data-note-status]'); if (status) status.textContent = noteDraft === value ? cart.dataset.noteSaved : ''; });
  }
  function renderSections(data, focus) {
    document.querySelectorAll('[data-cart-count]').forEach(el => { el.textContent = data.item_count; });
    const current = carts();
    for (const cart of current) {
      const html = data.sections?.[cart.dataset.sectionId];
      if (!html) throw new Error('');
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const replacement = parsed.querySelector(`lv-project-cart[data-context="${cart.dataset.context}"]`);
      if (!replacement) throw new Error('');
      cart.replaceWith(replacement);
    }
    syncNote();
    if (focus) {
      const cart = carts().find(el => el.dataset.context === focus.context);
      const line = [...(cart?.querySelectorAll('[data-line-key]') || [])].find(el => el.dataset.lineKey === focus.key);
      const target = line?.querySelector(focus.selector) || cart?.querySelector('.lv-cart-title');
      if (target) { if (!target.matches('button,input,a')) target.tabIndex = -1; target.focus({preventScroll:true}); }
    }
  }
  async function updateLine(cart, line, quantity, selector) {
    if (busy) return;
    setBusy(true); message('');
    const focus = {context:cart.dataset.context,key:line.dataset.lineKey,selector};
    const sectionIds = [...new Set(carts().map(el => el.dataset.sectionId))];
    try {
      await enqueue(async () => {
        await saveNote();
        const data = await post('cart/change.js', {id:line.dataset.lineKey,quantity,sections:sectionIds,sections_url:root()+'cart'});
        renderSections(data, focus);
      });
    } catch (error) {
      // Reconcile an ambiguous response without repeating a cart mutation.
      try {
        const [stateResponse, sectionResponse] = await Promise.all([
          fetch(root()+'cart.js',{headers:{Accept:'application/json'}}),
          fetch(root()+'cart?sections='+encodeURIComponent(sectionIds.join(',')),{headers:{Accept:'application/json'}})
        ]);
        if (!stateResponse.ok || !sectionResponse.ok) throw new Error('');
        const data = await stateResponse.json(); data.sections = await sectionResponse.json();
        renderSections(data, focus);
      } catch { /* Keep the form available so the customer can reload or submit it. */ }
      message(error.message || cart.dataset.error);
    } finally { setBusy(false); }
  }
  class ProjectCart extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController(); const options = {signal:this.controller.signal};
      this.dataset.enhanced = '';
      const field = this.querySelector('[name="note"]');
      if (field && noteDraft === undefined) { noteDraft = field.value; savedNote = field.value; }
      syncNote();
      this.addEventListener('input', event => {
        if (!event.target.matches('[name="note"]')) return;
        noteDraft = event.target.value; syncNote(); clearTimeout(noteTimer);
        noteTimer = setTimeout(() => enqueue(saveNote).catch(error => message(error.message || this.dataset.error)), 550);
      }, options);
      this.addEventListener('change', event => {
        if (!event.target.matches('[data-cart-quantity]')) return;
        const input = event.target, value = Number(input.value), min = Number(input.dataset.min), step = Number(input.step) || 1;
        if (input.value === '' || !Number.isInteger(value) || value < 0 || (value !== 0 && (value < min || (value-min)%step)) || (input.max && value > Number(input.max))) {
          input.value = input.defaultValue; input.reportValidity(); return;
        }
        updateLine(this,input.closest('[data-line-key]'),value,'[data-cart-quantity]');
      }, options);
      this.addEventListener('click', event => {
        const remove = event.target.closest('[data-cart-remove]'), stepButton = event.target.closest('[data-quantity-step]');
        if (!remove && !stepButton) return;
        event.preventDefault(); if (busy) return;
        const line = (remove || stepButton).closest('[data-line-key]'), input = line.querySelector('[data-cart-quantity]');
        const min = Number(input.dataset.min), step = Number(input.step) || 1;
        let value = remove ? 0 : Number(input.value) + Number(stepButton.dataset.quantityStep)*step;
        if (value < min) value = Number(stepButton?.dataset.quantityStep) === 1 ? min : 0;
        if (input.max && value > Number(input.max)) return;
        updateLine(this,line,value,remove ? '[data-cart-quantity]' : `[data-quantity-step="${stepButton.dataset.quantityStep}"]`);
      }, options);
      this.addEventListener('submit', async event => {
        event.preventDefault(); if (busy) return;
        setBusy(true); message('');
        try {
          await enqueue(saveNote);
          // The native checkout submission retains Shopify's payment flow.
          if (event.submitter?.name === 'checkout') {
            const form = this.querySelector('form');
            let hidden = form.querySelector('input[name="checkout"]');
            if (!hidden) { hidden = document.createElement('input'); hidden.type = 'hidden'; hidden.name = 'checkout'; form.append(hidden); }
            hidden.value = 'checkout';
            HTMLFormElement.prototype.submit.call(form);
          } else { setBusy(false); }
        } catch(error) { message(error.message || this.dataset.error); setBusy(false); }
      }, options);
      const folder = this.querySelector('[data-folder-float]');
      if (folder) {
        let hoverOpened = false;
        folder.addEventListener('pointerenter', event => { if (event.pointerType === 'mouse' && !folder.open) { folder.open = true; hoverOpened = true; } }, options);
        folder.addEventListener('pointerleave', () => { if (hoverOpened && !folder.contains(document.activeElement)) { folder.open = false; hoverOpened = false; } }, options);
        folder.addEventListener('keydown', event => { if (event.key === 'Escape') { event.preventDefault(); folder.open = false; folder.querySelector('summary').focus(); } }, options);
        folder.addEventListener('click', event => {
          const link = event.target.closest('[data-folder-select]'); if (!link) return;
          event.preventDefault(); folder.open = false;
          const line = document.getElementById(link.hash.slice(1));
          line?.scrollIntoView({behavior:matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',block:'center'}); line?.focus({preventScroll:true});
        }, options);
      }
    }
    disconnectedCallback() { this.controller?.abort(); this.controller = null; }
  }
  customElements.define('lv-project-cart', ProjectCart);
  window.addEventListener('pageshow', event => { if (event.persisted) setBusy(false); });
})();
